/**
 * Badge Component - Reusable badge/label for feature pages
 * Used for status indicators, counts, and labels
 * 
 * Usage:
 * <Badge variant="primary">New</Badge>
 * <Badge variant="success">Active</Badge>
 * <Badge variant="danger">Expired</Badge>
 */

import React from 'react';
import { Box } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

const Badge = React.forwardRef(
  (
    {
      variant = 'default',
      size = 'md',
      children,
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    const { semantic, radiusPx, typography } = useFeatureTheme();

    const variantStyles = {
      default: {
        backgroundColor: semantic.surfaces.surfaceMuted,
        color: semantic.text.primary,
      },
      primary: {
        backgroundColor: semantic.interactive.primarySoft,
        color: semantic.interactive.primary,
      },
      success: {
        backgroundColor: semantic.states.successSoft,
        color: semantic.states.success,
      },
      warning: {
        backgroundColor: semantic.states.warningSoft,
        color: semantic.states.warning,
      },
      danger: {
        backgroundColor: semantic.states.dangerSoft,
        color: semantic.states.danger,
      },
      info: {
        backgroundColor: semantic.states.infoSoft,
        color: semantic.states.info,
      },
      neutral: {
        backgroundColor: semantic.surfaces.surfaceMuted,
        color: semantic.text.secondary,
      },
      outline: {
        backgroundColor: 'transparent',
        color: semantic.text.primary,
        border: `1px solid ${semantic.borders.border}`,
      },
    };

    const sizeStyles = {
      sm: {
        fontSize: typography.fontSizeXs,
        padding: '2px 6px',
      },
      md: {
        fontSize: typography.fontSizeSm,
        padding: '3px 8px',
      },
      lg: {
        fontSize: typography.fontSizeMd,
        padding: '4px 10px',
      },
    };

    return (
      <Box
        ref={ref}
        component="span"
        className={className}
        sx={{
          ...variantStyles[variant],
          ...sizeStyles[size],
          borderRadius: radiusPx.pill,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          ...sx,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
